import type { VoidComponent } from "solid-js";
import { Show, createMemo, For } from "solid-js";
import type { VersionHistorySidebarItemType } from "./VersionHistorySidebarItem.tsx";
import VersionHistorySidebarItem, { discardableItemTypes, restorableItemTypes } from "./VersionHistorySidebarItem.tsx";
import type { User } from "../../../../state/user.tsx";
import { RadioGroup } from "@ark-ui/solid";
import { isNil } from "lodash-es";
import { useModalManager } from "../../../../components/modal/ModalManager.tsx";
import { WarningModal } from "../../../../components/modal/Modal.tsx";
import { useTranslation } from "../../../../state/i18n.tsx";
import type { FormVersion } from "../../../../state/formVersion.tsx";
import { useFormVersionState, useFormVersionDispatch } from "../../../../state/formVersion.tsx";
import { Icon } from "../../../../components/icon/Icon.tsx";
import FormVersionId from "../../../../state/FormVersionId.ts";

export type HistoryItemAuthor = { type: "user"; user: User } | { type: "apiUser"; name?: string };

export type HistoryItem = {
  type: VersionHistorySidebarItemType;
  formVersion: FormVersion;
  lastUpdatedAt: Date;
  author?: HistoryItemAuthor;
};

type VersionHistorySidebarProps = {
  ref?: (el: HTMLDivElement) => void;
  items: HistoryItem[];
  attemptToLoadMore: () => void;
  selectedFormVersion: FormVersion | null;
  setSelectedFormVersion: (formVersion: FormVersion) => void;
  hasReachedEnd: boolean;
  isFetchingMore: boolean;
};

const VersionHistorySidebar: VoidComponent<VersionHistorySidebarProps> = (props) => {
  const { t } = useTranslation();
  const { open } = useModalManager();
  const formVersion = useFormVersionState();
  const setFormVersionStore = useFormVersionDispatch();

  const mappedItems = createMemo(() =>
    props.items.reduce<Record<string, HistoryItem>>((prev, curr) => {
      prev[curr.formVersion.id.toString()] = curr;
      return prev;
    }, {}),
  );

  const restoreAsDraft = (item: HistoryItem) => {
    const restoredFormVersion: FormVersion = {
      ...item.formVersion,
      meta: { ...item.formVersion.meta, status: "DRAFT" },
      id: formVersion.id,
      lastUpdatedAt: new Date(),
    };
    setFormVersionStore(restoredFormVersion);
    props.setSelectedFormVersion({ ...restoredFormVersion, id: FormVersionId.from("unsaved") });
  };
  const openRestoreModal = (item: HistoryItem) =>
    open({
      content: () => (
        <WarningModal
          title={t(($) => $.VERSION_HISTORY_RESTORE_MODAL_TITLE)}
          description={t(($) => $.VERSION_HISTORY_RESTORE_MODAL_DESCRIPTION)}
          cancelLabel={t(($) => $.VERSION_HISTORY_RESTORE_MODAL_CANCEL_LABEL)}
          confirmLabel={t(($) => $.VERSION_HISTORY_RESTORE_MODAL_CONFIRM_LABEL)}
          onConfirm={() => restoreAsDraft(item)}
        />
      ),
    });

  const discardUnsavedChanges = () => {
    const lastSavedFormVersion: FormVersion = {
      ...props.items[1].formVersion,
      id: formVersion.id,
      lastUpdatedAt: undefined,
    };
    setFormVersionStore(lastSavedFormVersion);

    // The effect in FormVersionService updates the lastUpdatedAt, so it needs to be cleared
    queueMicrotask(() => {
      setFormVersionStore("lastUpdatedAt", undefined);
    });

    props.setSelectedFormVersion({ ...lastSavedFormVersion, id: FormVersionId.from("saved") });
  };
  const openDiscardModal = () =>
    open({
      content: () => (
        <WarningModal
          title={t(($) => $.VERSION_HISTORY_DISCARD_MODAL_TITLE)}
          description={t(($) => $.VERSION_HISTORY_DISCARD_MODAL_DESCRIPTION)}
          cancelLabel={t(($) => $.VERSION_HISTORY_DISCARD_MODAL_CANCEL_LABEL)}
          confirmLabel={t(($) => $.VERSION_HISTORY_DISCARD_MODAL_CONFIRM_LABEL)}
          onConfirm={discardUnsavedChanges}
        />
      ),
    });

  return (
    <div
      class="h-full w-80 overflow-y-auto border-r border-subtle bg-surface-default pt-4 pr-2 pl-4"
      ref={(el: HTMLDivElement) => {
        props.ref?.(el);
      }}
    >
      <RadioGroup.Root
        orientation="vertical"
        defaultValue={props.selectedFormVersion?.id.toString()}
        value={props.selectedFormVersion?.id.toString()}
        onValueChange={(e) => {
          if (isNil(e.value)) {
            return;
          }
          props.setSelectedFormVersion(mappedItems()[e.value].formVersion);
        }}
      >
        <For each={props.items}>
          {(item, index) => (
            <VersionHistorySidebarItem
              type={item.type}
              formVersionId={item.formVersion.id}
              lastUpdatedAt={item.lastUpdatedAt}
              author={item.author}
              isFirstItem={index() === 0}
              isLastItem={index() === props.items.length - 1}
              selected={props.selectedFormVersion?.id.equals(item.formVersion.id) ?? false}
              onClick={() => {
                if (discardableItemTypes.includes(item.type)) {
                  openDiscardModal();
                } else if (restorableItemTypes.includes(item.type)) {
                  openRestoreModal(item);
                }
              }}
            />
          )}
        </For>
      </RadioGroup.Root>
      <Show when={props.isFetchingMore}>
        <div class="flex flex-row items-center gap-2 pt-4 pb-9.5 pl-8">
          <Icon name="spinner" size="md" type="solid" class="animate-spin icon-secondary" />
          <span class="text-base-regular text-secondary">
            {t(($) => $.VERSION_HISTORY_SIDEBAR_BOTTOM_LOADING_MORE)}
          </span>
        </div>
      </Show>
      <Show when={props.hasReachedEnd}>
        <div class="flex flex-col items-center gap-3.5 pt-3 pb-6">
          <div class="w-full border-b border-subtle" />
          <span class="text-base-regular text-secondary">{t(($) => $.VERSION_HISTORY_SIDEBAR_BOTTOM_REACHED_END)}</span>
        </div>
      </Show>
    </div>
  );
};

export default VersionHistorySidebar;
